import * as styles from './styles.css';
import { useSelector } from 'react-redux';
import { TApplicationState } from 'shared/common/types/redux';
import cn from 'clsx';
import { ArticleHeading2, Button, useDeviceType } from '@cian/ui-kit';
import { useWithModalForm } from 'shared/common/hooks/useWithModalForm';
import { EModalSource } from 'shared/common/types/modal';
import { clickGoToAgentConsultation, clickSellerConsultation } from 'shared/landingSeller/components/tracking';
import { useApplicationContext } from 'shared/common/utils/applicationContext';
import { useEffect, useState } from 'react';

export const StickyPromo = ({ landing }: { landing: 'seller' | 'buyer' }) => {
  const { custom } = useApplicationContext();
  const { handleOpenModal } = useWithModalForm();

  const device = useDeviceType();
  const enabled = useSelector((state: TApplicationState) => state.stickyPromo);
  const isJivoBotEnabled = useSelector((state: TApplicationState) => state.config.isJivoBotEnabled);
  const [isJivoLoaded, setIsJivoLoaded] = useState(false);
  const showJivoConsultation = landing === 'seller' && isJivoBotEnabled;

  useEffect(() => {
    if (!isJivoBotEnabled || !showJivoConsultation) {
      return;
    }

    const checkJivoLoaded = () => {
      if (window.jivo_api) {
        setIsJivoLoaded(true);

        return true;
      }

      return false;
    };

    if (checkJivoLoaded()) {
      return;
    }

    window.jivo_onLoadCallback = () => {
      setIsJivoLoaded(true);
    };

    const pollInterval = setInterval(() => {
      if (checkJivoLoaded()) {
        clearInterval(pollInterval);
      }
    }, 100);

    return () => {
      clearInterval(pollInterval);
    };
  }, [isJivoBotEnabled, showJivoConsultation]);

  const handleConsultation = () => {
    if (showJivoConsultation && isJivoBotEnabled) {
      clickSellerConsultation(custom, 'consultation_jivo');
      const style = document.getElementById('jivo-css-hide');
      if (style && style.parentNode) {
        style.parentNode.removeChild(style);
      }

      window.jivo_api?.show?.();
      window.jivo_api?.open?.({ start: 'chat' });

      window.jivo_onClose = () => {
        try {
          window.jivo_api?.hide?.();

          if (!document.getElementById('jivo-css-hide')) {
            const styleNode = document.createElement('style');
            styleNode.id = 'jivo-css-hide';
            styleNode.textContent =
              '#jvLabelWrap, #jcont, #jivo_web_widget, jdiv[class*="globalClass"], jdiv[class*="wrap__"], jdiv[class*="__jivoMobileButton"] { display: none !important; visibility: hidden !important; opacity: 0 !important; pointer-events: none !important; }';
            document.head.appendChild(styleNode);
          }
        } catch (e) {
          // noop
        }
      };
    } else {
      handleOpenModal(EModalSource.Consultation);
    }
  };

  const handleGoToAgentConsultation = () => {
    clickGoToAgentConsultation(custom, 'FindAgentFooter');
    const url = custom.source
      ? `https://www.cian.ru/step-by-step-forms/?formType=sdelka-agent-selection&cian_source=${custom.source}&cpd_message_entrypoint=landingPageFindAgentFooter`
      : `https://www.cian.ru/step-by-step-forms/?formType=sdelka-agent-selection&cpd_message_entrypoint=landingPageFindAgentFooter`;

    window.open(url);
  };

  const isJivoLoading = showJivoConsultation && isJivoBotEnabled && !isJivoLoaded;

  return (
    <div
      className={cn(
        styles['wrapper'],
        enabled && styles['active'],
        styles[landing],
        isJivoLoading && styles['jivo-loading'],
      )}
      data-testid="StickyPromo"
    >
      <div className={cn(styles.textblock, styles[device], styles[landing])}>
        <div className={styles['image']} />
        {/* eslint-disable-next-line no-irregular-whitespace */}
        {landing === 'seller' ? (
          <ArticleHeading2>Не тратьте время — продайте с&nbsp;профи</ArticleHeading2>
        ) : (
          <ArticleHeading2>Не тратьте время — доверьтесь&nbsp;профи</ArticleHeading2>
        )}
      </div>
      <div className={cn(styles.actions, styles[device], styles[landing])}>
        <Button theme="fill_primary" size="M" onClick={handleGoToAgentConsultation} fullWidth={device === 'phone'}>
          Подобрать агента
        </Button>

        <Button theme="fill_secondary" size="M" onClick={handleConsultation} fullWidth={device === 'phone'}>
          {showJivoConsultation && isJivoBotEnabled ? 'Задать вопрос в чате' : 'Получить консультацию'}
        </Button>
      </div>
    </div>
  );
};
